import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTheme } from '../../context/ThemeContext';
import PrimaryButton from './PrimaryButton';
import SecondaryButton from './SecondaryButton';
import DangerButton from './DangerButton';

export type ConfirmDialogVariant = 'confirm' | 'danger' | 'info' | 'success';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const variantConfig: Record<ConfirmDialogVariant, {
  icon: React.ElementType;
  colorKey: 'primary' | 'danger' | 'success' | 'warning';
}> = {
  confirm: { icon: WarningAmberIcon, colorKey: 'warning' },
  danger: { icon: ErrorOutlineIcon, colorKey: 'danger' },
  info: { icon: InfoOutlinedIcon, colorKey: 'primary' },
  success: { icon: CheckCircleOutlineIcon, colorKey: 'success' },
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'confirm',
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const { theme } = useTheme();
  const config = variantConfig[variant];
  const Icon = config.icon;

  const colorMap: Record<string, string> = {
    primary: theme.primary,
    danger: theme.danger,
    success: theme.success,
    warning: theme.warning ?? theme.primary,
  };
  const accentColor = colorMap[config.colorKey];

  const ConfirmBtn = variant === 'danger' ? DangerButton : PrimaryButton;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          backgroundColor: theme.components.modal.background,
          border: theme.components.modal.border
            ? `1px solid ${theme.components.modal.border}`
            : undefined,
        },
      }}
      data-testid="confirm-dialog"
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: '10px',
              backgroundColor: accentColor + '18',
              flexShrink: 0,
            }}
          >
            <Icon sx={{ color: accentColor, fontSize: 22 }} />
          </Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: theme.text, fontSize: '1.05rem' }}
          >
            {title}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {typeof message === 'string' ? (
          <Typography sx={{ color: theme.gray[600], lineHeight: 1.6 }}>
            {message}
          </Typography>
        ) : (
          message
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <SecondaryButton
          onClick={onCancel}
          disabled={loading}
          data-testid="confirm-dialog-cancel"
        >
          {cancelLabel}
        </SecondaryButton>
        <ConfirmBtn
          onClick={onConfirm}
          loading={loading}
          data-testid="confirm-dialog-confirm"
        >
          {confirmLabel}
        </ConfirmBtn>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
